<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <div class="card" id="report-card">
            <div id="report-body" class="card-body w-100">
                <div v-if="isInitialLoad" class="w-100 pt-2 pb-2">
                    <i
                        >Please select the
                        <a @click="openFilterModal" class="text-link">filter</a>
                    </i>
                </div>
                <template v-else>
                    <div>
                        <p class="item-name">Summary</p>
                        <QTable
                            v-if="!summaryData.length"
                            :columns="headerSummary"
                            :data="[]"
                            hide-bottom
                            :loading="loading.summary"
                            :rows-per-page-options="[0]"
                        >
                            <template v-slot:bottom-row>
                                <q-tr v-if="loading.summary">
                                    <q-td colspan="8">Loading summary...</q-td>
                                </q-tr>
                                <q-tr
                                    v-else-if="
                                        !isLoading && !isInitialLoad && !isError
                                    "
                                >
                                    <q-td colspan="8"
                                        >No records available</q-td
                                    >
                                </q-tr>
                            </template>
                        </QTable>
                        <template v-else>
                            <QTable
                                id="summary-table"
                                :data="summaryData"
                                :columns="headerSummary"
                                :loading="loading.summary"
                                :rows-per-page-options="[0]"
                                row-key="index"
                                hide-bottom
                            />
                        </template>
                    </div>

                    <p class="item-name">
                        Details
                    </p>
                    <QTable
                        v-if="!detailsData.length"
                        :columns="headerDetails"
                        :data="[]"
                        hide-bottom
                        :loading="loading.details"
                        :rows-per-page-options="[0]"
                        class="mb-4"
                    >
                        <template v-slot:bottom-row>
                            <q-tr v-if="loading.details">
                                <q-td colspan="8"
                                    >Loading details...
                                    {{ dataLoadedString }}</q-td
                                >
                            </q-tr>
                            <q-tr
                                v-else-if="
                                    !isLoading && !isInitialLoad && !isError
                                "
                            >
                                <q-td colspan="8">No records available</q-td>
                            </q-tr>
                        </template>
                    </QTable>
                    <template v-else>
                        <QTable
                            id="details-table"
                            :data="detailsData"
                            :columns="headerDetails"
                            :loading="loading.details"
                            :rows-per-page-options="[0]"
                            row-key="index"
                            hide-bottom
                            class="mb-4"
                        >
                            <template
                                v-slot:body-cell-appointment_datetime="{ row }"
                            >
                                <td class="text-left align-top">
                                    {{ row.appointment_datetime }} <br />
                                    {{ row.appointment_day }}
                                </td>
                            </template>

                            <template v-slot:body-cell-patient_name="{ row }">
                                <td class="text-left align-top">
                                    <patient-name
                                        v-if="row.patient_id"
                                        :patient="mapPatient(row)"
                                        open-new
                                    />
                                </td>
                            </template>

                            <template v-slot:body-cell-billing_detail="{ row }">
                                <td class="text-left align-top">
                                    <span
                                        v-for="billing in row.billing_detail"
                                        :key="billing.index"
                                    >
                                        <a
                                            v-if="billing.view_url"
                                            target="_blank"
                                            :href="
                                                generateInvoiceUrl(
                                                    billing.view_url
                                                )
                                            "
                                            >{{ billing.label }} ({{
                                                billing.total_amount
                                            }})</a
                                        >
                                        <span v-else
                                            >{{ billing.label }} ({{
                                                billing.total_amount
                                            }})</span
                                        >
                                        <br />
                                    </span>
                                </td>
                            </template>
                        </QTable>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { QTable } from "quasar";
import PatientName from "@/components/commons/patient-name";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";
import { isEmpty } from "lodash";

export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header"),
        PatientName
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("month"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            detailsData: [],
            title: "Appointment Duration",
            dateRange: "",
            isInitialLoad: true,
            loading: {
                summary: false,
                details: false
            },
            isError: false,
            indexAxios: 0,
            abortController: [],
            summaryData: []
        };
    },
    mounted() {
        let self = this;
        self.data.reportId = CREPORT.TYPE.APPOINTMENT_DURATION;
        self.data.filterDateOption = true;
        self.data.isShowProvider = false;
        self.data.isShowDateRange = true;
        self.data.datePickerOptionDefault = 3;

        self._getSetting().then(() => {
            self.data.start = self.getStartDate("month");
            self.data.end = self.getEndDate("day");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.togglehideActions(true);

            // if query is not empty, load the report
            // else open the filter modal before loading the report
            if (
                !isEmpty(this.$route.query.to) &&
                !isEmpty(this.$route.query.from)
            ) {
                self.data.from = this.$route.query.from;
                self.data.to = this.$route.query.to;
                self.init();
            } else {
                self.openFilterModal();
            }
        });

        self.$root.$on("report-filter-date-range:refresh", self.prepareData);

        self.$root.$on("appointment-duration:refresh", function () {
            if (self.abortController.length > 0) {
                self.abortController[self.indexAxios].abort();
            }
            self.indexAxios++;
            self.detailsData = [];
            self.summaryData = [];

            self.togglehideActions(true);
            self.resetPagination();
            self.init(self.indexAxios, 1);
        });

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.isShowDateTimeRange = false;
        this.data.datePickerOptionDefault = null;
        this.data.reportId = null;
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
        this.$root.$off("appointment-duration:refresh");
        if (this.abortController.length > 0) {
            this.abortController[this.indexAxios].abort();
        }
    },
    methods: {
        generateInvoiceUrl(viewUrl) {
            return `${window.location.origin}${viewUrl}`;
        },
        handleError(indexAxios, error) {
            if (!this.abortController[indexAxios].signal.aborted) {
                console.error(error.message);
                this.loading.summary = false;
                this.loading.details = false;
                this.loadingCompleted();
                this.togglehideActions(false);
                this.isError = true;
                this._message("Error: Unable to get the report", "danger");
            }
        },
        formatDate(value) {
            return value
                ? this._toDateTime(parseInt(value)).toFormat(this._dateFormat)
                : "";
        },
        prepareData(params) {
            if (this.abortController.length > 0) {
                this.abortController[this.indexAxios].abort();
            }
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
            }
            this.indexAxios++;
            this.detailsData = [];
            this.summaryData = [];
            this.togglehideActions(true);
            this.resetPagination();
            this.init(this.indexAxios);
        },
        async init(indexAxios, isRefresh = 0) {
            let self = this;
            self.loading.summary = true;
            self.loading.details = true;
            self.isError = false;
            self.isInitialLoad = false;

            self.pagination.page = 1;
            self.abortController[indexAxios] = new AbortController();
            self.summaryData = [];
            self.detailsData = [];
            self.dateRange = self.getDateRange(self.data.from, self.data.to);
            self.loadingPercentages();

            await ReportService.getReport(CREPORT.TYPE.APPOINTMENT_DURATION, {
                from: self.data.from,
                to: self.data.to,
                signal: self.abortController[indexAxios].signal,
                show_results: false,
                refresh: isRefresh
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.dateRange = self.getDateRange(
                            res.resource.meta.from,
                            res.resource.meta.to
                        );

                        if (!isEmpty(res.resource.summary.results)) {
                            self.generateResultSummary(res.resource.summary);
                            self.getDetails(indexAxios, isRefresh);
                        } else {
                            self.togglehideActions(false);
                            self.loading.details = false;
                        }

                        self.loading.summary = false;
                    } else if (res && res.queue) {
                        let dateTo = self.data.to;
                        let dateFrom = self.data.from;
                        let query = self.$route.query;

                        self._message(res.message, "warning");

                        if (
                            !(
                                query &&
                                query.to == dateTo &&
                                query.from == dateFrom
                            )
                        ) {
                            self.$router.replace({
                                query: { to: dateTo, from: dateFrom }
                            });
                        }

                        self.togglehideActions(false);

                        self.loading.summary = false;
                        self.loading.details = false;
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.log(e);
                    self.handleError(indexAxios, e);
                })
                .finally(() => {
                    self.dynamicPageTitle(self.title);
                });
        },
        async getDetails(indexAxios, isRefresh = 0) {
            let self = this;
            let details = [];
            self.pagination.page = 1;
            self.pagination.lastPage = 1; // Last page will be updated in first API

            while (self.pagination.page <= self.pagination.lastPage) {
                self.loadingPercentages();

                try {
                    let res = await ReportService.getReport(
                        CREPORT.TYPE.APPOINTMENT_DURATION,
                        {
                            from: self.data.from,
                            to: self.data.to,
                            page: self.pagination.page,
                            perPage: 50,
                            signal: self.abortController[indexAxios].signal,
                            show_results: true,
                            refresh: isRefresh
                        }
                    );

                    if (res && res.message && res.message.code === 200) {
                        const { data, pagination } = res.resource.results;

                        details.push(...data);

                        // Update the pagination last page
                        if (self.pagination.page === 1) {
                            self.pagination.lastPage = pagination.last_page;
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                    self.pagination.page++;
                } catch (e) {
                    this.handleError(indexAxios, e);
                    break;
                }
            }

            this.detailsData = details;

            this.loadingCompleted();
            this.togglehideActions(false);

            this.loading.details = false;
        },
        generateResultSummary(details) {
            const { results, total_results: totalResults } = details;

            if (!isEmpty(results)) {
                this.summaryData = results;
                this.summaryData.push({
                    appointment_type: "Total",
                    ...totalResults
                });
            }
        },
        togglehideActions(value) {
            this.defaultToggleHideActions(value);
        },
        mapPatient(row) {
            if (!row.patient_id) {
                return null;
            }

            return {
                name: row.patient_name,
                encoded_id: row.patient_encoded_id
            };
        }
    },
    computed: {
        isLoading() {
            return this.loading.summary || this.loading.details;
        },
        dataLoadedString() {
            return `(${this.pagination.current}%)`;
        },
        headerSummary() {
            return [
                {
                    name: "appointment_type",
                    field: "appointment_type",
                    label: "Type",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "check_in",
                    field: "check_in",
                    label: "Check In",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "admitted",
                    field: "admitted",
                    label: "Admitted",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "in_room",
                    field: "in_room",
                    label: "In Room",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "visit_complete",
                    field: "visit_complete",
                    label: "Visit Complete",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "avg_total_duration",
                    field: "avg_total_duration",
                    label: "Avg Total Time",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "median_total_duration",
                    field: "median_total_duration",
                    label: "Median Total Time",
                    align: "center",
                    style: "vertical-align: top"
                },
                {
                    name: "total_duration",
                    field: "total_duration",
                    label: "Total Time",
                    align: "center",
                    style: "vertical-align: top"
                }
            ];
        },
        headerDetails() {
            return [
                {
                    name: "appointment_datetime",
                    field: "appointment_datetime",
                    label: "Date / Time",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "appointment_type",
                    field: "appointment_type",
                    label: "Type",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "complaint",
                    field: "complaint",
                    label: "Complaint",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "patient_name",
                    field: "patient_name",
                    label: "Patient",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "assigned_to",
                    field: "assigned_to",
                    label: "Assigned To",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "provider_name",
                    field: "provider_name",
                    label: "Provider",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "check_in",
                    field: "check_in",
                    label: "Check In",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "admitted",
                    field: "admitted",
                    label: "Admitted",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "in_room",
                    field: "in_room",
                    label: "In Room",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "visit_complete",
                    field: "visit_complete",
                    label: "Visit Complete",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "total_duration",
                    field: "total_duration",
                    label: "Total Time",
                    align: "left",
                    style: "vertical-align: top"
                },
                {
                    name: "billing_detail",
                    field: "billing_detail",
                    label: "Billing",
                    align: "left",
                    style: "vertical-align: top"
                }
            ];
        }
    }
};
</script>

<style type="text/css">
#details-table table thead tr th,
#summary-table table thead tr th,
#summary-table table tbody tr:last-child {
    font-weight: bold !important;
}
</style>
